import React, { forwardRef } from 'react';
import { Modal } from 'react-bootstrap';
import { ModalHeader } from '../ModalHeader.js';
import { Button } from '../Button';
import { i18n } from '../../i18n/translate';
// @ts-ignore
import logo from '../../images/everfi_logo.svg';

type UserData = {
  organization_name?: string;
  organization_id?: string;
  first_name?: string;
  last_name?: string;
};

interface Props {
  cookieEnabled: boolean;
  copySuccess: boolean;
  copyToClipboard: () => void;
  currentUrl: string;
  getOS: () => any;
  getUserTypes: (userData: UserData) => any;
  helpMessage: string;
  helpMessageEnabled: boolean;
  isFoundry: boolean;
  showHelpModal: boolean;
  toggleHelpModal: () => void;
  userData?: any;
}

const HelpModalBody = forwardRef<HTMLInputElement, Props>(
  (
    {
      cookieEnabled,
      copySuccess,
      copyToClipboard,
      currentUrl,
      getOS,
      getUserTypes,
      helpMessage,
      helpMessageEnabled,
      isFoundry,
      showHelpModal,
      toggleHelpModal,
      userData,
    },
    ref
  ) => {
    return (
      <Modal
        animation={false}
        /* @ts-ignore */
        container={ref}
        aria-labelledby="help-modal-title"
        className="help-modal modal-size-medium"
        dialogClassName="foundry"
        id="help-modal"
        show={showHelpModal}
      >
        {/* Remove when below is TypeScript-ified */
        /* @ts-ignore */}
        <ModalHeader
          canClickOff={true}
          closeButton={true}
          onCancel={toggleHelpModal}
          title={i18n.t('Need Help?')}
          titleId="help-modal-title"
        />
        <Modal.Body>
          <h5>{i18n.t('System Requirements')}</h5>
          <p>
            {i18n.t('For best performance, use our')}{' '}
            <a
              href="https://everfi.com/minimum-system-requirements"
              target="_blank"
              className="f-700 branded-link"
              rel="noopener noreferrer"
            >
              {i18n.t('minimum system requirements')}
            </a>
            .
          </p>
          <h5>{i18n.t('Technical Support')}</h5>

          {!!helpMessage && helpMessageEnabled /* Custom Support Info */ && (
            <div dangerouslySetInnerHTML={{ __html: helpMessage }} />
          )}
          {!helpMessageEnabled && isFoundry /* Foundry Default Support Info */ && (
            <p>
              {i18n.t('You can get help by accessing our')}{' '}
              <a
                href="https://support.everfi.com"
                target="_blank"
                className="f-700 branded-link"
                rel="noopener noreferrer"
              >
                {i18n.t('technical support site')}
              </a>{' '}
              {i18n.t('to read FAQs or chat with our support team.')}
            </p>
          )}
          {!helpMessageEnabled && !isFoundry /* Next Default Support Info */ && (
            <p>
              {i18n.t('For technical support with EVERFI course content, please email:')}{' '}
              <a href="mailto:learnersupport@everfi.com" className="f-700 branded-link">
                {i18n.t('learnersupport@everfi.com')}
              </a>{' '}
            </p>
          )}

          <p>{i18n.t('Please copy and paste the information below into your support message:')}</p>
          <section id="help-modal-info">
            <div>
              {i18n.t('Platform')}:{' '}
              <span className="f-500" data-info-value="platform">
                {i18n.t('Foundry')}
              </span>{' '}
            </div>
            {!!userData && (
              <React.Fragment>
                {!!userData.organization_name && isFoundry && (
                  <div>
                    {i18n.t('Organization name')}:{' '}
                    <span className="f-700" data-info-value="organization_name">
                      {userData.organization_name}
                    </span>{' '}
                  </div>
                )}
                {!!userData.organization_id && isFoundry && (
                  <div>
                    {i18n.t('Organization ID')}:{' '}
                    <span className="f-700" data-info-value="organization_id">
                      {userData.organization_id}
                    </span>{' '}
                  </div>
                )}
                <div>
                  {i18n.t('Operating system')}:{' '}
                  <span className="f-700" data-info-value="operating_system">
                    {getOS()}. {i18n.t('Cookies are {{cookieEnabled}}', { cookieEnabled })}.{' '}
                    {i18n.t('Javascript is enabled')}.{' '}
                  </span>
                </div>
                <br />
                {(!!userData.first_name || !!userData.last_name) && (
                  <div>
                    {i18n.t('Name')}:{' '}
                    <span
                      className="f-700"
                      data-info-value="user_name"
                    >{`${userData.first_name} ${userData.last_name}`}</span>{' '}
                  </div>
                )}
                {!!getUserTypes(userData) && (
                  <div>
                    {i18n.t('User Type')}:{' '}
                    <span className="f-700" data-info-value="user_type">
                      {getUserTypes(userData)}
                    </span>{' '}
                  </div>
                )}
              </React.Fragment>
            )}
            <div>
              {i18n.t('Current URL')}:{' '}
              <a href={currentUrl} className="f-700 branded-link">
                {currentUrl}
              </a>
            </div>
          </section>
          <Button
            disabled={copySuccess}
            onClick={() => copyToClipboard()}
            data-action="copy"
            style="secondary"
            branded={true}
            icon={copySuccess ? 'check' : 'copy'}
            label={copySuccess ? i18n.t('Copy Successful!') : i18n.t('Copy')}
          />
          {copySuccess === false && <span className="error"> {i18n.t('Copy unsuccessful, please copy manually')}</span>}
          <div className="modal-logo">
            <img src={logo} alt={i18n.t('EVERFI logo')} />
          </div>
        </Modal.Body>
      </Modal>
    );
  }
);
HelpModalBody.displayName = 'HelpModalBody';

export { HelpModalBody };
